import {FC, useEffect, useRef} from 'react';
import {Box, Button, Flex, Stack} from '@chakra-ui/react';
import {api, EPromptType, MS_PER_CHAR, useT} from '@kcast-fe/core';
import {useForm} from 'react-hook-form';
import {APP_VARS, IPersonalizationForm} from 'core';
import {Loader, Modal, useModal} from 'ui-kit';

import {
  AssistantFieldsForm,
  AvatarUpload,
  BaseFieldsForm,
  LiveStreamSettings,
  NotificationsSetting,
  PromptsForm
} from './components';

const MAX_WIDTH = {base: '796px', smToMd: 'calc(100% - 12px)'};

const PersonalizationSceneFC: FC = () => {
  const {data: defaultPrompts = []} = api.prompts.useDefaultPromptsQuery();
  const {data: prompts, isFetching: isPromptsFetching} = api.prompts.usePromptsQuery();
  const {data: settings, isFetching: isSettingsFetching} = api.settings.useSettingsQuery();

  const promptsMutation = api.prompts.useUpdatePromptsMutation();
  const chatbotMutation = api.settings.useUpdateChatbotSettingsMutation();

  const {closeModal} = useModal();
  const {t} = useT();

  const wasInit = useRef(false);
  const initialPromptsRef = useRef<Map<EPromptType, {custom: boolean; prompt: string}>>(new Map());

  const {control, reset, handleSubmit, formState} = useForm<IPersonalizationForm>({
    defaultValues: {
      prompts: [],
      assistant_name: '',
      model_name_chat: '',
      model_name_voice: '',
      model_name_video: '',
      ms_per_char: String(MS_PER_CHAR)
    }
  });

  useEffect(() => {
    if (!wasInit.current && prompts && settings) {
      wasInit.current = true;
      const userPrompts = prompts.filter((p) => p.name === EPromptType.ChatSystem);
      const formPrompts = APP_VARS.FEATURE_FLAGS.ALL_PROMPTS ? prompts : userPrompts;
      initialPromptsRef.current = new Map(
        formPrompts.map((p) => [p.name, {custom: p.custom, prompt: p.prompt}])
      );
      reset({
        assistant_name: settings.assistant.name,
        model_name_chat: settings.chatbot.model_name,
        model_name_voice: settings.voice_chatbot.model_name,
        model_name_video: settings.voice_vision_bot.model_name,
        ms_per_char: String(settings.typewriter_settings?.ms_per_char ?? MS_PER_CHAR),
        prompts: formPrompts
      });
    }
  }, [prompts, settings, reset]);

  const handleUpdate = async (data: IPersonalizationForm) => {
    const changedPrompts = data.prompts
      .filter((p) => {
        const init = initialPromptsRef.current.get(p.name);
        if (!init) return true;
        return p.custom !== init.custom || (p.custom && p.prompt !== init.prompt);
      })
      .map((p) => ({id: p.id, prompt: p.custom ? p.prompt : null}));

    if (changedPrompts.length > 0) {
      await promptsMutation.mutateAsync(changedPrompts);
    }

    await chatbotMutation.mutateAsync({
      assistant: {name: data.assistant_name},
      chatbot: {model_name: data.model_name_chat},
      voice_chatbot: {model_name: data.model_name_voice},
      voice_vision_bot: {model_name: data.model_name_video},
      typewriter_settings: {ms_per_char: Number(data.ms_per_char)}
    });

    closeModal();
  };

  const isUpdating = promptsMutation.isPending || chatbotMutation.isPending;
  const isFetching = isPromptsFetching || isSettingsFetching;

  return (
    <Modal isOpen isFullHeight title={t('common.personalization')} maxW={MAX_WIDTH}>
      <Box h={'calc(100% - 24px)'}>
        <Stack h={'100%'} gap={'0'} w={'100%'} overflowY={'hidden'}>
          <Flex pt={'24px'} justifySelf={'flex-start'} />
          <Box p={'0 20px'} minH={'1px'} flex={'1 1 auto'} overflowY={'auto'}>
            {isFetching ? (
              <Loader isCenter size={'lg'} />
            ) : (
              <Stack gap={'24px'}>
                <AvatarUpload />
                <AssistantFieldsForm control={control} />
                <LiveStreamSettings />
                <NotificationsSetting />
                <BaseFieldsForm control={control} />
                <PromptsForm control={control} defaultPrompts={defaultPrompts} />
              </Stack>
            )}
          </Box>

          <Flex justifyContent={'flex-end'} p={'24px 24px 8px 24px'} justifySelf={'flex-end'}>
            <Flex gap={'12px'}>
              <Button size={'lg'} variant={'subtle'} disabled={isUpdating} onClick={closeModal}>
                {t('actions.cancel')}
              </Button>
              <Button
                size={'lg'}
                variant={'solid'}
                disabled={isUpdating || !formState.isValid}
                onClick={handleSubmit(handleUpdate)}
              >
                {t('actions.save')}
              </Button>
            </Flex>
          </Flex>
        </Stack>
      </Box>
    </Modal>
  );
};

export const PersonalizationScene = PersonalizationSceneFC;
